<template>
  <md-dialog
    :md-active.sync="isVisible"
    :md-backdrop="true"
    :md-close-on-esc="false"
    :md-click-outside-to-close="false"
  >
    <md-dialog-title>Bulk Upload</md-dialog-title>

    <md-dialog-content>
      <input type="file" @change="handleFileUpload" />
      <md-progress-bar v-if="uploading" md-mode="indeterminate"></md-progress-bar>
      <div v-if="uploadError">{{ uploadError }}</div>
      <div v-if="validationErrors.length">
        <h4>Validation Errors:</h4>
        <ul>
          <li v-for="error in validationErrors" :key="error.rowIndex">
            Row {{ error.rowIndex + 1 }}: {{ error.message }}
          </li>
        </ul>
      </div>
      <div v-if="uploadResults.success > 0">
        Successfully uploaded {{ uploadResults.success }} records.
      </div>
      <div v-if="uploadResults.failed > 0">
        Failed to upload {{ uploadResults.failed }} records.
      </div>
    </md-dialog-content>

    <md-dialog-actions>
      <md-button class="md-primary" @click="downloadFailedTransactions()"
      v-if="failedTransactions.length > 0">Download Failed Transactions</md-button>
      <md-button class="md-warning" @click="close()">Cancel</md-button>
    </md-dialog-actions>
  </md-dialog>
</template>

<script>
import http from '../../utils/Http.util';

export default {
  computed: {
    userData() {
      return this.$store.state.userData;
    },
    isLive() {
      return this.$store.state.isLive;
    },
  },
  name: 'file-upload',
  data() {
    return {
      isVisible: false,
      file: null,
      uploading: false,
      uploadError: null,
      validationErrors: [],
      uploadResults: {
        success: 0,
        failed: 0,
      },
      failedTransactions: [],
    };
  },
  methods: {
    open() {
      this.isVisible = true;
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.uploading = true;
      this.uploadError = null;
      this.validationErrors = [];
      this.uploadResults = { success: 0, failed: 0 };

      this.parseCSV(file)
        .then((rows) => {
          this.validateRows(rows);
          if (this.validationErrors.length > 0) {
            this.uploading = false;
          } else {
            this.uploadData(rows);
          }
        })
        .catch(() => {
          this.uploadError = 'Failed to process file.';
          this.uploading = false;
        });
    },

    parseCSV(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const text = event.target.result;
          const rows = text.split('\n').map((row) => row.split(','));

          if (rows.length > 1) {
            const headers = rows[0].map((header) => header.trim().replace(/"/g, ''));

            const data = rows.slice(1).map((row) => headers.reduce((acc, header, index) => {
              acc[header] = row[index] ? row[index].trim().replace(/"/g, '') : null;
              return acc;
            }, {})).filter((row) => row.phone);

            resolve(data);
          } else {
            reject(new Error('No valid rows found'));
          }
        };
        reader.onerror = reject;
        reader.readAsText(file);
      });
    },

    validateRows(rows) {
      if (!Array.isArray(rows)) {
        this.uploadError = 'Parsed data is not an array.';
        return;
      }

      this.validationErrors = rows.map((row, index) => {
        if (typeof row !== 'object' || row === null) {
          return {
            rowIndex: index,
            message: 'Row data is not an object.',
          };
        }

        const { phone, reference } = row;
        if (!phone || !reference) {
          return {
            rowIndex: index,
            message: `Missing ${!phone ? 'phone' : 'reference'} field.`,
          };
        }
        return null;
      }).filter((error) => error !== null);
    },

    uploadData(rows) {
      this.failedTransactions = [];
      const uploadPromises = rows.map((row, index) => http.authReq('/dashboard/virtual-accounts/', row, 'post')
        .then(() => {
          this.uploadResults.success += 1;
        })
        .catch((error) => {
          this.uploadResults.failed += 1;
          this.failedTransactions.push({ rowIndex: index + 1, ...row, error: error.message });
        }));

      // Wait for all upload promises to settle
      Promise.allSettled(uploadPromises)
        .then(() => {
          this.uploading = false;
        });
    },

    close() {
      this.isVisible = false;
      this.file = null;
      this.uploading = false;
      this.uploadError = null;
      this.validationErrors = [];
      this.uploadResults = { success: 0, failed: 0 };
      this.failedTransactions = [];
    },

    downloadFailedTransactions() {
      if (this.failedTransactions.length > 0) {
        this.downloadCSV(this.failedTransactions, 'failed_transactions.csv');
      }
    },

    downloadCSV(data, filename = 'failed_transactions.csv') {
      // Convert JSON to CSV
      const header = Object.keys(data[0]);
      const csv = [
        header.join(','), // header row
        ...data.map((row) => header.map((fieldName) => JSON.stringify(row[fieldName], this.replacer)).join(',')),
      ].join('\r\n');

      // Create a Blob and trigger a download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      if (link.download !== undefined) { // feature detection
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    replacer(key, value) {
      return value === null ? '' : value;
    },
  },
};
</script>

<style lang="scss" scoped>
.pointer {
  cursor: pointer;
}
.pointer:hover {
  background-color: #edf2f7;
  color: white;
}
.md-dialog /deep/.md-dialog-container {
  min-width: 30%;
}
</style>
